import React, { useMemo } from 'react';

import { Space, Table } from 'antd-v5';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import { useMBDict } from '@dict/duck/hooks';
import CommentsButton from '@modules/advancement/unitAdvancement/components/CommentsButton/CommentsButton';
import { advancementTypes } from '@shared/constants';
import { getPreferredName } from '@shared/utils/preferredName';

import { filteredToApproveReqsSel } from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import MBImage from './MBImage';
import styles from './RequirementsTable.module.scss';

export default function RequirementsTable() {
  const { meritBadgesById } = useMBDict();

  const dispatch = useDispatch();
  const requirements = useSelector(filteredToApproveReqsSel);

  const rowSelection = useMemo(
    () => ({
      onChange(rowKeys: (string | number)[]) {
        dispatch(
          mbcDashboardActions.setSelectedRequirements(rowKeys as string[]),
        );
      },
    }),
    [dispatch],
  );

  return (
    <Table
      dataSource={requirements}
      rowKey={'rowKey'}
      rowSelection={rowSelection}
      scroll={{ x: true }}
      columns={[
        {
          title: <FormattedMessage id="mbcDashboard.youthTable.column.name" />,
          fixed: 'left',
          render(_value, record) {
            return (
              <Text className={styles.text}>
                {getPreferredName(record.youth)}
              </Text>
            );
          },
        },
        {
          title: <FormattedMessage id="mbcDashboard.youthTable.column.mid" />,
          minWidth: 120,
          render(_value, record) {
            return <Text className={styles.text}>{record.youth.memberId}</Text>;
          },
        },
        {
          title: (
            <FormattedMessage id="mbcDashboard.reqTable.column.requirement" />
          ),
          render(_value, record) {
            const mbRef = meritBadgesById.get(record.mb.meritBadgeId);
            const mbImage = mbRef?.imageUrl100;
            const req = record.requirement;
            const mbName = record.mb.name;
            const requirement = [
              'Requirement',
              req.number || req.listNumber,
            ].join(' ');

            return (
              <Space align="start">
                <MBImage src={mbImage} width={32} height={32} />
                <Text className={styles.text}>
                  {mbName} - {requirement}
                </Text>
              </Space>
            );
          },
        },
        {
          key: 'commentButton',
          dataIndex: 'commentButton',
          width: '80px',
          onCell: () => ({
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
            },
          }),
          render(_value, record) {
            return (
              <CommentsButton
                advancementId={record.mb.meritBadgeId}
                advancementType={advancementTypes.MERIT_BADGES}
                renderPhotos
                requirementId={record.requirement.id}
                requirementName={record.requirement.name}
                userId={record.youth.userId}
                versionId={record.mb.versionId}
              />
            );
          },
        },
        {
          title: (
            <FormattedMessage id="mbcDashboard.mbTable.column.completion" />
          ),
          render(_value, record) {
            const completionDate = record.requirement.dateCompleted;

            return <Text className={styles.text}>{completionDate}</Text>;
          },
        },
      ]}
    />
  );
}
